import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  Bot, 
  User, 
  MessageSquare, 
  Zap,
  Settings,
  RefreshCw,
  Copy,
  Download,
  Trash2,
  Type,
  Code,
  FileText,
  Globe,
  Server,
  TestTube,
  Smile,
} from 'lucide-react';
import { useEnhancedAppStore } from '@/stores/EnhancedAppStore';
import { ChatConfig, ChatMessage, ApiProvider } from '@/types';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

// 对话模板
const CHAT_TEMPLATES = [
  {
    id: 'greeting',
    name: '简单问候',
    description: '测试基本连接',
    content: '你好！请介绍一下你自己。',
    icon: <Smile className="w-4 h-4" />,
    category: 'basic',
  },
  {
    id: 'code-explanation',
    name: '代码解释',
    description: '请求解释代码功能',
    content: '请解释以下代码的功能和作用：\n```python\ndef fibonacci(n):\n    if n <= 1:\n        return n\n    return fibonacci(n-1) + fibonacci(n-2)\n```',
    icon: <Code className="w-4 h-4" />,
    category: 'code',
  },
  {
    id: 'text-summary',
    name: '文本摘要',
    description: '请求对文本进行摘要',
    content: '请对以下文本进行摘要：\n人工智能是研究、开发用于模拟、延伸和扩展人的智能的理论、方法、技术及应用系统的一门新的技术科学。人工智能企图了解智能的实质，并生产出一种新的能以人类智能相似的方式做出反应的智能机器，该领域的研究包括机器人、语言识别、图像识别、自然语言处理和专家系统等。',
    icon: <FileText className="w-4 h-4" />,
    category: 'text',
  },
  {
    id: 'creative-writing',
    name: '创意写作',
    description: '请求进行创意写作',
    content: '请写一个关于未来科技的短篇故事，包含人工智能和太空探索元素。',
    icon: <Type className="w-4 h-4" />,
    category: 'creative',
  },
  {
    id: 'api-test',
    name: 'API 测试',
    description: '测试 API 功能和参数',
    content: '请用 JSON 格式返回当前的时间和日期，以及一个随机数。',
    icon: <TestTube className="w-4 h-4" />,
    category: 'test',
  },
  {
    id: 'translation',
    name: '翻译测试',
    description: '测试多语言能力',
    content: '请将以下英文翻译成中文："The quick brown fox jumps over the lazy dog."',
    icon: <Globe className="w-4 h-4" />,
    category: 'language',
  },
];

// 模型参数配置
const MODEL_PARAMS = {
  temperature: {
    min: 0,
    max: 2,
    step: 0.1,
    defaultValue: 0.7,
    description: '控制输出的随机性，值越高输出越随机',
  },
  maxTokens: {
    min: 1,
    max: 4000,
    step: 1,
    defaultValue: 1000,
    description: '控制生成的最大令牌数',
  },
  topP: {
    min: 0,
    max: 1,
    step: 0.05,
    defaultValue: 1,
    description: '核采样参数，控制输出的多样性',
  },
  frequencyPenalty: {
    min: 0,
    max: 2,
    step: 0.1,
    defaultValue: 0,
    description: '降低重复令牌的概率',
  },
  presencePenalty: {
    min: 0,
    max: 2,
    step: 0.1,
    defaultValue: 0,
    description: '降低新话题的概率',
  },
};

interface MessageBubbleProps {
  message: ChatMessage;
  provider?: ApiProvider;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, provider }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  return (
    <div className={clsx(
      'flex gap-3 group',
      isUser && 'flex-row-reverse',
      isSystem && 'justify-center'
    )}>
      {/* 头像 */}
      <div className={clsx(
        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
        isUser ? 'bg-accent-100' :
        isAssistant ? 'bg-primary-100' :
        'bg-gray-100'
      )}>
        {isUser ? (
          <User className="w-4 h-4 text-accent-600" />
        ) : isAssistant ? (
          <Bot className="w-4 h-4 text-primary-600" />
        ) : (
          <Server className="w-4 h-4 text-gray-600" />
        )}
      </div>

      {/* 消息内容 */}
      <div className={clsx(
        'max-w-[70%] rounded-2xl px-4 py-3',
        isUser 
          ? 'bg-accent-600 text-white rounded-br-none' 
          : isAssistant
          ? 'bg-gray-100 text-gray-900 rounded-bl-none'
          : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
      )}>
        {/* 消息头部 */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="font-medium">
              {isUser ? '您' : isAssistant ? '助手' : '系统'}
            </div>
            {message.model && (
              <div className="text-xs px-2 py-1 bg-white/20 rounded">
                {message.model}
              </div>
            )}
            {message.tokens && (
              <div className="text-xs text-gray-500">
                {message.tokens} tokens
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={handleCopy}
              className="p-1 hover:bg-white/10 rounded"
              title="复制消息"
            >
              {isCopied ? (
                <CheckCircle className="w-3 h-3" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>

        {/* 消息内容 */}
        <div className={clsx(
          'prose prose-sm max-w-none',
          isAssistant && 'prose-gray',
          isUser && 'prose-invert'
        )}>
          {isAssistant ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                code({ className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  return match ? (
                    <div className="relative">
                      <div className="absolute top-2 right-2 text-xs text-gray-400">
                        {match[1]}
                      </div>
                      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    </div>
                  ) : (
                    <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            <div className="whitespace-pre-wrap">{message.content}</div>
          )}
        </div>

        {/* 消息底部 */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
          <div className="text-xs opacity-75">
            {new Date(message.timestamp).toLocaleTimeString()}
          </div>
          {provider && (
            <div className="text-xs opacity-75">
              via {provider.name}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ModelConfigPanelProps {
  config: ChatConfig;
  onConfigChange: (config: ChatConfig) => void;
  availableModels: string[];
  selectedModel: string;
  onModelChange: (model: string) => void;
}

const ModelConfigPanel: React.FC<ModelConfigPanelProps> = ({
  config,
  onConfigChange,
  availableModels,
  selectedModel,
  onModelChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">模型配置</h3>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-gray-600"
        >
          <ChevronDown className={clsx('w-5 h-5 transition-transform', isExpanded && 'rotate-180')} />
        </button>
      </div>

      {/* 模型选择 */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          选择模型
        </label>
        <div className="flex flex-wrap gap-2">
          {availableModels.map((model) => (
            <button
              key={model}
              onClick={() => onModelChange(model)}
              className={clsx(
                'px-3 py-2 rounded-lg border text-sm font-mono',
                selectedModel === model
                  ? 'bg-primary-100 text-primary-800 border-primary-300'
                  : 'bg-gray-100 text-gray-800 border-gray-200 hover:border-gray-300'
              )}
            >
              {model}
            </button>
          ))}
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          {Object.entries(MODEL_PARAMS).map(([key, param]) => {
            const value = config[key as keyof ChatConfig] as number;
            
            return (
              <div key={key}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-medium text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {param.description}
                    </div>
                  </div>
                  <div className="text-sm font-mono text-gray-900">
                    {value.toFixed(key === 'temperature' ? 1 : 0)}
                  </div>
                </div>
                <input
                  type="range"
                  min={param.min}
                  max={param.max}
                  step={param.step}
                  value={value}
                  onChange={(e) => onConfigChange({
                    ...config,
                    [key]: parseFloat(e.target.value),
                  })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{param.min}</span>
                  <span>{param.max}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const EnhancedChatTestPage: React.FC = () => {
  const {
    providers,
    sessions,
    currentProviderId,
    currentSessionId,
    sendChatMessage,
    testingProviders,
    providerStats,
  } = useEnhancedAppStore();
  
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [config, setConfig] = useState<ChatConfig>({
    temperature: 0.7,
    maxTokens: 1000,
    topP: 1,
    frequencyPenalty: 0,
    presencePenalty: 0,
    stream: false,
  });
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentProvider = providers.find(p => p.id === currentProviderId);
  const currentSession = sessions.find(s => s.id === currentSessionId);
  const availableModels = currentProvider?.models || [];

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentSession?.messages, streamingContent]);

  // 自动聚焦输入框
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 设置默认模型
  useEffect(() => {
    if (availableModels.length > 0 && !selectedModel) {
      const defaultModel = currentProvider?.defaultModel || availableModels[0];
      setSelectedModel(defaultModel);
    }
  }, [availableModels, currentProvider, selectedModel]);

  // 发送消息
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !currentSession || !currentProvider || isSending) return;
    
    setIsSending(true);
    
    try {
      const result = await sendChatMessage(
        currentProvider.id,
        currentSession.id,
        inputMessage,
        config
      );
      
      if (!result.success) {
        alert(`发送失败: ${result.error}`);
      }
      
      setInputMessage('');
    } catch (error) {
      alert('发送消息时发生错误: ' + (error instanceof Error ? error.message : '未知错误'));
    } finally {
      setIsSending(false);
    }
  };

  // 使用模板
  const handleUseTemplate = (content: string) => {
    setInputMessage(content);
    inputRef.current?.focus();
  };

  // 清空对话
  const handleClearConversation = () => {
    if (currentSession && confirm('确定要清空当前对话吗？')) {
      // 这里需要实现清空对话的逻辑
      alert('对话已清空');
    }
  };

  // 导出对话
  const handleExportConversation = () => {
    if (!currentSession) return;
    
    const exportData = {
      session: currentSession,
      provider: currentProvider,
      timestamp: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `对话导出_${currentSession.name}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 处理键盘快捷键
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    
    if (e.key === 'Escape') {
      setInputMessage('');
    }
    
    // Ctrl/Cmd + / 聚焦输入框
    if ((e.ctrlKey || e.metaKey) && e.key === '/') {
      e.preventDefault();
      inputRef.current?.focus();
    }
  };

  if (!currentProvider) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <Server className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">未选择供应商</h3>
          <p className="text-gray-500 mb-6">
            您需要先选择一个 API 供应商才能进行对话测试。
            请前往"供应商管理"页面选择或添加供应商。
          </p>
        </div>
      </div>
    );
  }

  if (!currentSession) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">未选择会话</h3>
          <p className="text-gray-500 mb-6">
            您需要先创建一个会话才能开始对话。
          </p>
        </div>
      </div>
    );
  }

  const currentProviderStats = currentProvider ? providerStats[currentProvider.id] : undefined;
  const isTesting = currentProvider ? testingProviders[currentProvider.id] : false;

  return (
    <div className="space-y-6">
      {/* 头部信息 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Server className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">{currentProvider.name}</div>
                <div className="text-sm text-gray-500">
                  {availableModels.length} 个可用模型
                </div>
              </div>
            </div>
            {providerStats && (
              <div className="flex items-center gap-2">
                {currentProviderStats?.lastSuccess ? (
                  <div className="w-4 h-4 text-green-500">✓</div>
                ) : (
                  <div className="w-4 h-4 text-red-500">✗</div>
                )}
                <div className="text-xs text-gray-500">
                  {currentProviderStats?.averageLatency ? `${currentProviderStats.averageLatency}ms` : '未测试'}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-accent-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">{currentSession.name}</div>
                <div className="text-sm text-gray-500">
                  {currentSession.messageCount} 条消息
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              最后活动: {new Date(currentSession.lastMessageAt).toLocaleTimeString()}
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {currentSession.tokenCount.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">总令牌数</div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Cpu className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* 左侧：配置面板 */}
        <div className="lg:col-span-1 space-y-4">
          {/* 模型配置面板 */}
          <ModelConfigPanel
            config={config}
            onConfigChange={setConfig}
            availableModels={availableModels}
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
          />

          {/* 对话模板 */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Copy className="w-5 h-5" />
              对话模板
            </h3>
            <div className="space-y-2">
              {CHAT_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleUseTemplate(template.content)}
                  className="w-full p-3 text-left rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    {template.icon}
                    <div className="font-medium text-gray-900">{template.name}</div>
                  </div>
                  <div className="text-sm text-gray-500">{template.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">操作</h3>
            <div className="space-y-2">
              <button
                onClick={handleClearConversation}
                className="w-full btn-secondary justify-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                清空对话
              </button>
              <button
                onClick={handleExportConversation}
                className="w-full btn-secondary justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                导出对话
              </button>
              <button
                onClick={() => window.open(currentProvider.baseUrl, '_blank')}
                className="w-full btn-secondary justify-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                打开 API 文档
              </button>
            </div>
          </div>
        </div>

        {/* 右侧：对话区域 */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="flex flex-col h-[600px]">
              {/* 消息区域 */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {currentSession.messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">开始对话</h3>
                    <p className="text-gray-500">
                      输入消息开始与 AI 对话，或从左侧选择模板
                    </p>
                    <div className="mt-6 grid grid-cols-2 gap-3 max-w-md mx-auto">
                      {CHAT_TEMPLATES.slice(0, 4).map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleUseTemplate(template.content)}
                          className="p-3 rounded-lg border border-gray-200 hover:border-primary-300 text-sm"
                        >
                          {template.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {currentSession.messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        provider={currentProvider}
                      />
                    ))}
                    {isSending && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-primary-600" />
                        </div>
                        <div className="bg-gray-100 text-gray-900 rounded-2xl rounded-bl-none px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="font-medium">助手</div>
                            <div className="text-xs px-2 py-1 bg-white/20 rounded">
                              {selectedModel}
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-150" />
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse delay-300" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* 输入区域 */}
              <div className="border-t border-gray-200 p-4">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <textarea
                      ref={inputRef}
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={`输入消息... (Enter 发送, Shift+Enter 换行, Esc 清空)`}
                      className="input min-h-[80px] resize-none font-mono"
                      rows={3}
                      disabled={isSending}
                      spellCheck={false}
                    />
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-500">
                        {inputMessage.length} 字符
                      </div>
                      <div className="text-xs text-gray-500">
                        {selectedModel && `使用模型: ${selectedModel}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim() || isSending}
                      className={clsx(
                        'btn-primary h-full px-6',
                        (!inputMessage.trim() || isSending) && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {isSending ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          发送中...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          发送
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setInputMessage('')}
                      className="btn-secondary"
                      title="清空输入 (Esc)"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 状态信息 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currentSession.messageCount}
                  </div>
                  <div className="text-sm text-gray-500">消息数</div>
                </div>
                <MessageSquare className="w-6 h-6 text-gray-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currentSession.tokenCount.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">令牌数</div>
                </div>
                <Hash className="w-6 h-6 text-gray-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {config.temperature.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-500">温度</div>
                </div>
                <Thermometer className="w-6 h-6 text-gray-400" />
              </div>
            </div>
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {config.maxTokens}
                  </div>
                  <div className="text-sm text-gray-500">最大令牌</div>
                </div>
                <Target className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 使用提示 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">使用提示</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Keyboard className="w-5 h-5 text-blue-600" />
              <div className="font-medium">键盘快捷键</div>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Enter: 发送消息</li>
              <li>• Shift+Enter: 换行</li>
              <li>• Esc: 清空输入</li>
              <li>• Ctrl+/: 聚焦输入框</li>
            </ul>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Settings className="w-5 h-5 text-green-600" />
              <div className="font-medium">参数调节</div>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 温度: 控制输出随机性</li>
              <li>• 最大令牌: 控制生成长度</li>
              <li>• Top P: 控制输出多样性</li>
              <li>• 频率惩罚: 减少重复内容</li>
            </ul>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-purple-600" />
              <div className="font-medium">高级功能</div>
            </div>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 代码高亮和格式化</li>
              <li>• Markdown 渲染支持</li>
              <li>• 对话导出为 JSON</li>
              <li>• 模板快速插入</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Keyboard = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M6 16h4M14 16h4" />
  </svg>
);